/**
 * Review — every question you've missed, anywhere in the app, waiting until you
 * beat it (answering it correctly clears it, via the progress context). "Drill
 * all" runs them through the QuizRunner. Ports renderReview.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { tests, type TrainingQuestion } from '@/content/training';
import { useTrainingProgress } from '../TrainingProgressContext';
import { trainingPath } from '../trainingNav';
import { PageHeader, Card, Button } from './TrainingUI';
import { QuizRunner } from './QuizRunner';

export function Review() {
  const navigate = useNavigate();
  const progress = useTrainingProgress();
  // The drill deck is SNAPSHOTTED when the drill starts. It must not track the
  // live missed set: answering correctly clears a question from `missed`, and a
  // shrinking `questions` prop would reshuffle QuizRunner mid-run (and unmount it
  // entirely on the last question, before its result shows).
  const [drill, setDrill] = useState<TrainingQuestion[] | null>(null);
  const missed = progress.missedList();

  if (drill) {
    return (
      <div className="mx-auto max-w-2xl">
        <QuizRunner
          key="review-drill"
          questions={drill}
          onAgain={() => setDrill(null)}
          onExit={() => navigate(trainingPath('home'))}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader
        title="Missed Questions"
        sub={
          missed.length
            ? "Every question you've gotten wrong lands here, and answering it correctly clears it. Empty list = ready."
            : "Nothing here. Miss a question anywhere in the app and it'll wait for you here until you beat it."
        }
      />
      {missed.length > 0 && (
        <>
          <Button variant="primary" className="mb-4" onClick={() => setDrill(missed)}>
            Drill all {missed.length} missed question{missed.length > 1 ? 's' : ''}
          </Button>
          <Card>
            {missed.map((q) => (
              <div key={q.id} className="review-item">
                <div style={{ fontWeight: 600 }}>{q.q}</div>
                <div className="muted" style={{ marginTop: 4 }}>
                  Answer:{' '}
                  <span style={{ color: 'var(--navy)' }}>{q.choices[q.answer]}</span> ·{' '}
                  {tests[q.test].name} · {q.topic}
                </div>
              </div>
            ))}
          </Card>
        </>
      )}
    </div>
  );
}
